/**
 * HooksConfigPanel Component
 *
 * Main panel for configuring agent hooks using functional grouping.
 * Combines PresetSelector and FeatureGroupCards for a progressive configuration experience.
 *
 * Design Principles:
 * - Functional grouping instead of stage-based (SYSTEM/USER/FINAL)
 * - Progressive disclosure: start with presets, then customize via groups
 * - Wide-screen friendly: 2-column grid layout for feature groups
 * - Show dependencies between hooks visually
 */

import React, { useMemo, useCallback } from 'react';
import { Settings2 } from 'lucide-react';
import { PresetSelector } from './PresetSelector';
import { FeatureGroupCard } from './FeatureGroupCard';
import {
  type SelectablePresetId,
  type HookId,
  HOOKS_REGISTRY,
  getFeatureGroupsOrdered,
  inferPresetFromConfig,
  isHookEnabled,
} from '@/config/hooks-registry';
import { getConfigValue } from '@/config/fieldConfig';
import type { AgentConfiguration } from '@/types';
import type { TabId } from '@/config/tabConfig';

/**
 * Set a value at a nested path in an object (immutable)
 */
function setConfigValue<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown
): T {
  const keys = path.split('.');
  const result = { ...obj } as Record<string, unknown>;
  let current = result;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    current[key] = { ...(current[key] as Record<string, unknown> || {}) };
    current = current[key] as Record<string, unknown>;
  }

  current[keys[keys.length - 1]] = value;
  return result as T;
}

interface HooksConfigPanelProps {
  config: Partial<AgentConfiguration>;
  /** Update the entire config object */
  onConfigUpdate: (newConfig: Partial<AgentConfiguration>) => void;
  /** Navigate to a specific configuration tab, optionally to a specific section */
  onNavigateToTab?: (tabId: TabId, sectionId?: string) => void;
  /** App context with model lists for auto-selection */
  appContext?: Record<string, unknown>;
  /** Class name for styling */
  className?: string;
}

export const HooksConfigPanel: React.FC<HooksConfigPanelProps> = ({
  config,
  onConfigUpdate,
  onNavigateToTab,
  appContext,
  className = '',
}) => {
  // Get ordered feature groups
  const featureGroups = useMemo(() => getFeatureGroupsOrdered(), []);

  // Calculate current preset based on enabled hooks
  const currentPreset = useMemo(() => {
    const enabledHooks = new Set<HookId>();
    for (const hook of Object.values(HOOKS_REGISTRY)) {
      const value = getConfigValue(config, hook.configPath);
      if (isHookEnabled(hook.id, value)) {
        enabledHooks.add(hook.id);
      }
    }
    return inferPresetFromConfig(enabledHooks);
  }, [config]);

  // Handle preset change - updates multiple hook configs at once
  const handlePresetChange = useCallback(
    (presetId: SelectablePresetId, presetConfig: Record<string, boolean | string>) => {
      let newConfig = { ...config };

      // Apply each config path from the preset
      for (const [path, value] of Object.entries(presetConfig)) {
        newConfig = setConfigValue(newConfig, path, value);
      }

      // Also update the config_preset field
      newConfig = setConfigValue(newConfig, 'config_preset', presetId);

      onConfigUpdate(newConfig);
    },
    [config, onConfigUpdate]
  );

  // Handle individual config change
  const handleConfigChange = useCallback(
    (path: string, value: unknown) => {
      const newConfig = setConfigValue(config, path, value);
      onConfigUpdate(newConfig);
    },
    [config, onConfigUpdate]
  );

  // Count total enabled hooks
  const totalEnabledCount = useMemo(() => {
    let count = 0;
    for (const hook of Object.values(HOOKS_REGISTRY)) {
      const value = getConfigValue(config, hook.configPath);
      if (isHookEnabled(hook.id, value)) {
        count++;
      }
    }
    return count;
  }, [config]);

  const totalHooks = Object.keys(HOOKS_REGISTRY).length;

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">功能配置</h2>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            已启用 <span className="font-medium text-gray-900">{totalEnabledCount}</span>/{totalHooks} 个功能
          </span>
        </div>
      </div>

      {/* Preset Selector */}
      <PresetSelector
        currentPreset={currentPreset}
        onPresetChange={handlePresetChange}
      />

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 border-t border-gray-200" />
        <span className="text-xs text-gray-400 whitespace-nowrap">或自定义各功能组</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>

      {/* Feature Group Cards - 2 column grid on wide screens */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {featureGroups.map((group) => (
          <FeatureGroupCard
            key={group.id}
            group={group}
            config={config}
            onConfigChange={handleConfigChange}
            onNavigateToTab={onNavigateToTab}
            appContext={appContext}
          />
        ))}
      </div>

      {/* Footer hint */}
      <div className="text-xs text-gray-500 text-center space-y-1">
        <p>点击开关可启用/禁用功能 • 带有「配置」按钮的功能可进入详细设置</p>
        <p className="text-gray-400">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 bg-amber-400 rounded-full" /> 依赖：需要先启用依赖的功能
          </span>
          <span className="mx-2">•</span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 bg-blue-400 rounded-full" /> 被依赖：有其他功能依赖此功能
          </span>
        </p>
      </div>
    </div>
  );
};

export default HooksConfigPanel;
